import React, {Suspense, useState, useEffect} from "react";
import get from "lodash/get";
import compose from "lodash/flowRight";
import {connect} from "react-redux";
import {withRouter} from "react-router";
import {Redirect, Route, Switch} from "react-router-dom";

import Auth from "shared/Auth/auth";
import {Box, PatronThemeProvider, Spinner} from "@unibuddy/patron";
import DesignSystemProvider from "shared/Experimental/Utils/components/DesignSystemProvider/DesignSystemProvider";
import {
    isFeatureEnabledByUnibuddyOrByUnibuddyAndUniversity,
    useFeatureFlags,
} from "shared/Utils/FeatureUtils";

import AppBody from "./modules/App/AppBody";
import AppRoutes from "./AppRoutes";
import AppContainer from "./modules/App/AppContainer";
import Login from "./modules/Authenticate/containers/Login";
import Logout from "./modules/Authenticate/containers/Logout";
import PasswordResetRoutes from "./modules/PasswordReset/PasswordResetRoutes";
import EnterpriseAppRoutes from "./modules/EnterpriseMgtTools/EnterpriseAppRoutes";
import AuthRoutesContainer from "./modules/Authenticate/containers/AuthRoutesContainer";
import MemberOrganisationsQuery from "./modules/Navbar/components/MemberOrganisations/MemberOrganisationsQuery";
import InstitutionNavBar from "./modules/EnterpriseMgtTools/containers/NavbarEnterprise/InstitutionNavBar";
import {EnterpriseErrorContextProvider} from "./modules/EnterpriseMgtTools/containers/NavbarEnterprise/EnterpriseErrorContext";
import {enterpriseTheme} from "./modules/InstitutionAnalytics/general/components/FontTheme";
import {adminTheme} from "./modules/Utils/theme/theme";

import DevTools from "./modules/Devtools/DevTools";
import {COMMUNITY_STAND_ALONE} from "./modules/Utils/FeatureFlagConstants";

const PublicRoutes = ({match}) => {
    return (
        <AuthRoutesContainer>
            <Suspense fallback={null}>
                <Switch>
                    <Route path={`${match.url}/login`} component={Login} />
                    <Route path={`${match.url}/logout`} component={Logout} />
                    <Route path={`${match.url}/reset`} component={PasswordResetRoutes} />
                </Switch>
            </Suspense>
        </AuthRoutesContainer>
    );
};

export const AuthenticatedRoutes = compose(
    withRouter,
    connect(({authState}) => ({authState})),
)(({authState}) => {
    if (!Auth.loggedIn(authState, "university")) {
        return (
            <Redirect
                to={{
                    pathname: "/auth/login",
                    state: {
                        nextPathname: location.pathname,
                    },
                }}
            />
        );
    }
    const [open, setOpen] = useState(false);
    const [enterpriseNavOpen, setEnterpriseNavOpen] = useState(false);
    const [communityStandAloneEnabled, setCommunityStandAloneEnabled] = useState(false);
    const uniUserGroups = get(authState, "me.universityUser.groups", []);

    // Used to determine if the user is a super admin or not
    const organisationUserGroup = uniUserGroups.filter(group => group.type === "organisation_user");
    const university = get(authState, "me.universityUser.university", {});

    const {loading, error, data: universityFeatureData} = useFeatureFlags(university.slug);
    const universityFeatures = universityFeatureData
        ? universityFeatureData.university.config.features
        : [];

    const toggle = () => {
        setOpen(!open);
    };

    const toggleEnterpriseNav = () => {
        setEnterpriseNavOpen(!enterpriseNavOpen);
    };

    useEffect(
        () => {
            if (universityFeatures.length) {
                setCommunityStandAloneEnabled(
                    isFeatureEnabledByUnibuddyOrByUnibuddyAndUniversity(
                        universityFeatures,
                        COMMUNITY_STAND_ALONE,
                    ),
                );
            }
        },
        [universityFeatures],
    );
    if (loading) {
        return <Box><Spinner/></Box>;
    }
    if (error) {
        return <div>Unable to fetch features. Please try after sometime</div>;
    }

    return (
        <DesignSystemProvider>
            <EnterpriseErrorContextProvider>
                <AppContainer>
                    {organisationUserGroup.length && !communityStandAloneEnabled ? (
                        <MemberOrganisationsQuery
                            render={({memberOrganisations}) => {
                                memberOrganisations.sort((org1, org2) =>
                                    org1.name.toLowerCase().localeCompare(org2.name.toLowerCase()),
                                );
                                return (
                                    <PatronThemeProvider theme={enterpriseTheme}>
                                        <InstitutionNavBar
                                            university={university}
                                            memberOrganisations={memberOrganisations}
                                            isOpen={open}
                                            isEnterpriseNavOpen={enterpriseNavOpen}
                                        />
                                    </PatronThemeProvider>
                                );
                            }}
                        />
                    ) : null}
                    <AppBody
                        isInstitution={
                            organisationUserGroup &&
                            organisationUserGroup.length &&
                            !communityStandAloneEnabled
                        }
                    >
                        <Switch>
                            {!communityStandAloneEnabled ? (
                                <Route
                                    path="/enterprise-management-tools"
                                    render={props => (
                                        <EnterpriseAppRoutes
                                            enterpriseNavOpen={enterpriseNavOpen}
                                            toggle={toggleEnterpriseNav}
                                            {...props}
                                        />
                                    )}
                                />
                            ) : null}
                            <Route
                                path="/"
                                render={props => (
                                    <PatronThemeProvider theme={adminTheme}>
                                        <AppRoutes
                                            isInstitution={
                                                organisationUserGroup &&
                                                organisationUserGroup.length &&
                                                !communityStandAloneEnabled
                                            }
                                            isOpen={open}
                                            toggle={toggle}
                                            {...props}
                                        />
                                    </PatronThemeProvider>
                                )}
                            />
                        </Switch>
                    </AppBody>
                </AppContainer>
            </EnterpriseErrorContextProvider>
        </DesignSystemProvider>
    );
});

const MainRoutes = () => (
    <Switch>
        <Route path="/devtools" component={DevTools} />
        <Route path="/auth" component={PublicRoutes} />
        <Route component={AuthenticatedRoutes} />
    </Switch>
);

export default MainRoutes;
